"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import type { ITrainingRequest } from "../../../shared/models/TrainingRequestModel"
import { LoadingEllipsis } from "../../../shared/components/loading/Loading"
import type TrainingRequest from "../../../shared/models/TrainingRequestModel"
import type { IUser } from "../../../shared/models/User"
import { GenerateTrainingRequestsPDF } from "./generateTrainingRequestsPDF"
import generateCSV from "./generateCSV"

const Reports = observer(() => {
  const { api, store, ui } = useAppContext()
  const [trainingRequests, setTrainingRequests] = useState<ITrainingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<IUser[]>([])
  const [filterCriteria, setFilterCriteria] = useState("")
  const [filterValue, setFilterValue] = useState("")

  const departments = store.department.all

  const getUserName = (uid: string): string => {
    const user = users.find((user) => user.uid === uid)
    return user ? user.displayName || "Unknown User" : "Unknown User"
  }

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }

  const getName = (uid: string): string => {
    const currentUser = users.find((user) => user.uid === uid)
    return currentUser?.displayName || "Unknown Supervisor"
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-300"
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-300"
      case "rejected":
        return "bg-red-100 text-red-800 border-red-300"
      default:
        return "bg-gray-100 text-gray-800 border-gray-300"
    }
  }

  useEffect(() => {
    const fetchTrainingRequests = async () => {
      setLoading(true)
      setError(null)

      try {
        await api.trainingRequest.getAll()
        const requests = store.trainingRequest.all as TrainingRequest[]

        await api.user.getAll()

        const allUsers = store.user.all.map((user) => user.asJson) as IUser[]
        setUsers(allUsers)

        const formattedRequests: ITrainingRequest[] = requests.map((req) => ({
          id: req.asJson.id,
          uid: req.asJson.uid,
          courseName: req.asJson.courseName,
          startDate: req.asJson.startDate,
          endDate: req.asJson.endDate,
          certificateObtained: req.asJson.certificateObtained,
          institution: req.asJson.institution,
          comments: req.asJson.comments,
          name: req.asJson.name,
          email: req.asJson.email,
          department: req.asJson.department,
          phoneNumber: req.asJson.phoneNumber,
          location: req.asJson.location,
          supervisor: req.asJson.supervisor,
          fax: req.asJson.fax,
          supervisorPhone: req.asJson.supervisorPhone,
          preferredDate: req.asJson.preferredDate,
          offeredBy: req.asJson.offeredBy,
          courseLocation: req.asJson.courseLocation,
          courseSummary: req.asJson.courseSummary,
          requestType: req.asJson.requestType,
          duration: req.asJson.duration,
          purpose: req.asJson.purpose,
          dateRequested: req.asJson.dateRequested,
          confirmation: req.asJson.confirmation,
          status: req.asJson.status,
          trainingType: req.asJson.trainingType,
          budget: req.asJson.budget,
          attachment: req.asJson.attachment,
        }))

        setTrainingRequests(formattedRequests)
      } catch (err) {
        console.error(err)
        setError("Failed to load training requests.")
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load training requests.",
          type: "danger",
        })
      } finally {
        setLoading(false)
      }
    }

    fetchTrainingRequests()
  }, [api.trainingRequest, store.trainingRequest, ui.snackbar])

  const filteredRequests = trainingRequests.filter((request) => {
    if (!filterCriteria || !filterValue) return true

    switch (filterCriteria) {
      case "courseName":
        return request.courseName.toLowerCase().includes(filterValue.toLowerCase())
      case "status":
        return request.status.toLowerCase() === filterValue.toLowerCase()
      case "department":
        return getDepartmentName(request.department).toLowerCase().includes(filterValue.toLowerCase())
      default:
        return true
    }
  })

  const handleGeneratePDF = async () => {
    if (filteredRequests.length > 0) {
      GenerateTrainingRequestsPDF("Training Requests Report", trainingRequests, users, departments)
    } else {
      ui.snackbar.load({
        id: Date.now(),
        message: "No training requests available to export.",
        type: "warning",
      })
    }
  }

  const handleExportCSV = () => {
    generateCSV("Training Requests", trainingRequests, users, departments)
  }

  if (loading) return <LoadingEllipsis />
  if (error)
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    )

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h3 className="text-3xl font-bold text-gray-800 mb-2">Training Requests Report</h3>
        <div className="h-1 w-32 bg-[#84cbe9] rounded"></div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter By</label>
            <select
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-[#84cbe9] transition-colors"
              value={filterCriteria}
              onChange={(e) => setFilterCriteria(e.target.value)}
            >
              <option value="">Select Filter Criteria</option>
              <option value="courseName">Course Name</option>
              <option value="status">Status</option>
              <option value="department">Department</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Value</label>
            <input
              type="text"
              placeholder="Enter filter value..."
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-[#84cbe9] transition-colors"
            />
          </div>

          <div className="flex gap-3">
            <button
              className="px-6 py-2.5 bg-[#84cbe9] text-white font-medium rounded-lg hover:bg-[#3f6a8e] transition-colors duration-200 shadow-sm"
              onClick={handleGeneratePDF}
            >
              Export PDF
            </button>
            <button
              className="px-6 py-2.5 bg-[#e7a637] text-white font-medium rounded-lg hover:bg-[#d69520] transition-colors duration-200 shadow-sm"
              onClick={handleExportCSV}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredRequests.length}</span> of{" "}
            <span className="font-semibold text-gray-900">{trainingRequests.length}</span> requests
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#3f6a8e] text-white">
                <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Course Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Start Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">End Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Institution</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Department</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Supervisor</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Duration</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-[#e4eefd] transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900">{getUserName(request.uid)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{request.courseName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(request.startDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(request.endDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(request.status)}`}
                    >
                      {request.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{request.institution}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{getDepartmentName(request.department)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{getName(request.supervisor)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{request.duration}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{request.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRequests.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No training requests found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  )
})

export default Reports
